import Assignment from "@mui/icons-material/Assignment";
import {
	Box,
	MenuItem,
	Stack,
	TextField
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { VARIABLE_DATA_TYPE_OPTIONS } from "@/constants/rule-variables";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import AddConditionButton from "../buttons/AddConditionButton";
import RaisedTrashIconButton from "../buttons/RaisedTrashIconButton";
import type { Option } from "../constants";
import AssignmentOperatorsSelect from "../fields/AssignmentOperatorsSelect";
import type {
	DatabaseTableOptionV2,
	ResourceCategoryV2,
	VariableGroupV2,
	VisibleGroupKey,
} from "../fields/form-entity-select";
import FormEntitySelect from "../fields/form-entity-select";
import SimpleVariableSelect from "../fields/SimpleVariableSelect";
import type { RuleVariable, SimpleVariableInput } from "../hooks/useRuleVariables";
import { getUniqueDefaultName } from "../nodes/nodeFactory";
import {
	getAssignmentValueFormEntityBinding,
} from "../utils/assignment-statement-display-utils";
import { buildFormulaVariableRef, buildScreenFieldRef, selectedFieldMetaFromFormulaVariable, selectedFieldMetaFromScreenField, type FormulaVariableRef, type ScreenFieldRef } from "../utils/data-field-type-utils";
import {
	inferValueCategoryFromEntityFields,
	isDecisionValueEntityRow,
} from "../utils/decision-statement-value-utils";
import { DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE } from "../utils/node-name-uniqueness";
import BaseForm from "./BaseForm";
import FormStatementContainer from "./FormStatementContainer";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

function optionDataTypeLabel(dataTypeId?: number): string | undefined {
	if (dataTypeId == null) return undefined;
	return VARIABLE_DATA_TYPE_OPTIONS.find((o) => Number(o.id) === Number(dataTypeId))?.label;
}

export interface AssignmentStatementRow {
	id: string;
	variable: string;
	operator: string;
	/** Bound to FormEntitySelect; for literals mirrors plainTextValue. */
	value: string;
	plainTextValue?: string;
	valueDatabaseTableFieldId?: string;
	valueDatabaseTableField?: { name: string };
	valueScreenFieldId?: string;
	valueScreenField?: ScreenFieldRef;
	valueScreenTableField?: ScreenFieldRef;
	valueDatabaseTableFieldName?: string;
	valueScreenFieldName?: string;
	valueSimpleVariableName?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueFormulaVariableName?: string;
	valueFormulaVariable?: FormulaVariableRef;
	valueDatabaseSingleRowVariableId?: string;
	valueDatabaseSingleRowVariableName?: string;
	valueGlobalConstant?: string;
	valueGlobalConstantName?: string;
	/** Drives `FormEntitySelect` selectedMeta / pill (same role as `SimpleVariableFormState.valueCategory`). */
	valueCategory?: ResourceCategoryV2;
}

export interface AssignmentFormState {
	assignmentName: string;
	statements: AssignmentStatementRow[];
}

export interface AssignmentFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: AssignmentFormState) => void;
	initialState?: Partial<AssignmentFormState>;
	isSaving?: boolean;
	simpleVariables?: RuleVariable[];
	existingVariables?: RuleVariable[];
	onAddSimpleVariable?: (input: SimpleVariableInput) => RuleVariable | null;
	operatorOptions?: Option[];
	/** When true, assignment operators come from the rule builder (getFormMetadata), not a separate GET. */
	suppressAssignmentOperatorsAutoFetch?: boolean;
	getDuplicateNameError?: (name: string) => string | undefined;
	/**
	 * Graph-wide reaction / assignment / decision names (lowercase keys), excluding the active node's
	 * name when editing — same pattern as LoopForm's `takenLoopNames`.
	 */
	takenReactionAssignmentDecisionNames?: Set<string>;
	platformId?: string | number;
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	globalConstantOptions?: Option[];
	ruleSimpleVariables?: RuleVariable[];
	ruleFormulaVariables?: RuleVariable[];
	ruleDatabaseSingleRowVariables?: RuleVariable[];
	ruleDatabaseMultiRowVariables?: RuleVariable[];
}

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_VARIABLE_GROUPS: VariableGroupV2[] = [];
const EMPTY_DATABASE_TABLES: DatabaseTableOptionV2[] = [];

const AssignmentForm = (props: AssignmentFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		simpleVariables = [],
		existingVariables,
		onAddSimpleVariable,
		operatorOptions,
		suppressAssignmentOperatorsAutoFetch = false,
		getDuplicateNameError,
		takenReactionAssignmentDecisionNames,
		platformId,
		resourceVariableGroups = EMPTY_VARIABLE_GROUPS,
		formulaVariableOptions = EMPTY_OPTIONS,
		resourceScreenFields = EMPTY_OPTIONS,
		globalConstantOptions = EMPTY_OPTIONS,
		ruleSimpleVariables = [],
		ruleFormulaVariables = [],
		ruleDatabaseSingleRowVariables = [],
		ruleDatabaseMultiRowVariables = [],
	} = props;

	const authHeader = useAuthHeader();
	const resolvedOperatorOptions = operatorOptions ?? [];

	const [fetchedScreenFields, setFetchedScreenFields] = useState<Option[]>([]);
	const [ruleVariableSingleRowTables, setRuleVariableSingleRowTables] = useState<DatabaseTableOptionV2[]>([]);
	const [ruleVariableMultiRowTables, setRuleVariableMultiRowTables] = useState<DatabaseTableOptionV2[]>([]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchResourceOptions = async () => {
			try {
				const tablesResponse = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
				if (cancelled) return;
				const rawGroups = tablesResponse.data?.tableGroups ?? [];

				const screenTables: { id: number; name: string }[] = [];

				for (const group of rawGroups) {
					if (group.screenTables) screenTables.push(...group.screenTables);
				}

				const screenFieldResults = await Promise.all(
					screenTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getScreenTableFields(table.id), { headers: authHeader });
							return (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: `${table.name}.${field.name}`,
								value: String(field.id),
							}));
						} catch {
							return [];
						}
					}),
				);
				if (cancelled) return;
				setFetchedScreenFields(screenFieldResults.flat());
			} catch (error) {
				console.error("Error fetching resource options:", error);
			}
		};

		fetchResourceOptions();
		return () => { cancelled = true; };
	}, [open, platformId, authHeader]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchRuleDatabaseVariableFields = async () => {
			const mapVars = async (vars: RuleVariable[]) =>
				Promise.all(
					vars.map(async (v) => {
						const tableId = v.source;
						if (!tableId) {
							return { label: v.name, value: v.id, fields: [] as Option[] };
						}
						try {
							const res = await axios.get(apiRoutes.getDatabaseTableFields(tableId), { headers: authHeader });
							const fields: Option[] = (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: field.name,
								value: String(field.id),
							}));
							return { label: v.name, value: v.id, fields };
						} catch {
							return { label: v.name, value: v.id, fields: [] as Option[] };
						}
					}),
				);

			try {
				const [single, multi] = await Promise.all([
					mapVars(ruleDatabaseSingleRowVariables),
					mapVars(ruleDatabaseMultiRowVariables),
				]);
				if (cancelled) return;
				setRuleVariableSingleRowTables(single);
				setRuleVariableMultiRowTables(multi);
			} catch (error) {
				console.error("Error fetching database variable fields:", error);
			}
		};

		fetchRuleDatabaseVariableFields();
		return () => { cancelled = true; };
	}, [open, platformId, ruleDatabaseSingleRowVariables, ruleDatabaseMultiRowVariables, authHeader]);

	const mergedScreenFields = useMemo(
		() => (resourceScreenFields.length ? resourceScreenFields : fetchedScreenFields),
		[resourceScreenFields, fetchedScreenFields],
	);

	const resourceVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys: VisibleGroupKey[] = [];
		if (mergedScreenFields.length) keys.push("screenFields");
		const hasSimpleVariables = resourceVariableGroups.some((g) => g.options.length > 0);
		if (hasSimpleVariables) keys.push("simpleVariable");
		if (formulaVariableOptions.length) keys.push("formulaVariable");
		if (ruleVariableSingleRowTables.length) keys.push("singleRowDatabaseVariables");
		if (ruleVariableMultiRowTables.length) keys.push("multiRowDatabaseVariables");
		return keys;
	}, [
		mergedScreenFields,
		resourceVariableGroups,
		formulaVariableOptions.length,
		ruleVariableSingleRowTables.length,
		ruleVariableMultiRowTables.length,
	]);

	const valueFieldVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys = [...resourceVisibleGroups];
		if (globalConstantOptions.length) keys.push("globalConstants");
		return keys;
	}, [resourceVisibleGroups, globalConstantOptions.length]);

	const getDefaultOperator = () => resolvedOperatorOptions[0]?.value ?? "";

	const buildStatement = (overrides?: Partial<AssignmentStatementRow>): AssignmentStatementRow => ({
		id: overrides?.id ?? uuidv4(),
		variable: overrides?.variable ?? "",
		operator: overrides?.operator ?? getDefaultOperator(),
		value: overrides?.value ?? "",
		plainTextValue: overrides?.plainTextValue,
		valueDatabaseTableFieldId: overrides?.valueDatabaseTableFieldId,
		valueScreenFieldId: overrides?.valueScreenFieldId,
		valueDatabaseTableField: overrides?.valueDatabaseTableField,
		valueScreenField: overrides?.valueScreenField,
		valueScreenTableField: overrides?.valueScreenTableField,
		valueDatabaseTableFieldName: overrides?.valueDatabaseTableFieldName,
		valueScreenFieldName: overrides?.valueScreenFieldName,
		valueSimpleVariableName: overrides?.valueSimpleVariableName,
		valueSimpleVariableId: overrides?.valueSimpleVariableId,
		valueFormulaVariableId: overrides?.valueFormulaVariableId,
		valueFormulaVariableName: overrides?.valueFormulaVariableName,
		valueFormulaVariable: overrides?.valueFormulaVariable,
		valueDatabaseSingleRowVariableId: overrides?.valueDatabaseSingleRowVariableId,
		valueDatabaseSingleRowVariableName: overrides?.valueDatabaseSingleRowVariableName,
		valueGlobalConstant: overrides?.valueGlobalConstant,
		valueGlobalConstantName: overrides?.valueGlobalConstantName,
		valueCategory:
			overrides?.valueCategory ??
			inferValueCategoryFromEntityFields({
				valueDatabaseTableFieldId: overrides?.valueDatabaseTableFieldId,
				valueScreenFieldId: overrides?.valueScreenFieldId,
				valueSimpleVariableId: overrides?.valueSimpleVariableId,
				valueFormulaVariableId: overrides?.valueFormulaVariableId,
				valueDatabaseSingleRowVariableId: overrides?.valueDatabaseSingleRowVariableId,
				valueGlobalConstant: overrides?.valueGlobalConstant,
				valueSimpleVariableName: overrides?.valueSimpleVariableName,
				valueFormulaVariableName: overrides?.valueFormulaVariableName,
				valueDatabaseSingleRowVariableName: overrides?.valueDatabaseSingleRowVariableName,
			}),
	});

	const buildInitialState = (): AssignmentFormState => ({
		assignmentName: initialState?.assignmentName
			? initialState.assignmentName
			: takenReactionAssignmentDecisionNames
				? getUniqueDefaultName("Assignment", takenReactionAssignmentDecisionNames, true)
				: "",
		statements: initialState?.statements?.length ? initialState.statements.map((s) => buildStatement(s)) : [buildStatement()]
	});

	const initialFormState = useMemo(buildInitialState, [
		initialState,
		resolvedOperatorOptions,
		simpleVariables,
		takenReactionAssignmentDecisionNames,
	]);

	const [formState, setFormState] = useState<AssignmentFormState>(initialFormState);

	const duplicateNameError = useMemo((): string | undefined => {
		const trimmed = formState.assignmentName.trim();
		const trimmedLower = trimmed.toLowerCase();
		if (!trimmed) return undefined;
		const fromProp = getDuplicateNameError?.(trimmed);
		if (fromProp) return fromProp;
		if (
			takenReactionAssignmentDecisionNames &&
			initialState?.assignmentName?.trim().toLowerCase() !== trimmedLower &&
			takenReactionAssignmentDecisionNames.has(trimmedLower)
		) {
			return DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE;
		}
		return undefined;
	}, [
		getDuplicateNameError,
		takenReactionAssignmentDecisionNames,
		formState.assignmentName,
		initialState?.assignmentName,
	]);

	const isSaveDisabled =
		!formState.assignmentName.trim() ||
		Boolean(duplicateNameError) ||
		formState.statements.some((s) => !s.variable || !s.operator || !s.value.trim());

	const updateStatement = (id: string, updates: Partial<Omit<AssignmentStatementRow, "id">>) => {
		setFormState((previous) => ({
			...previous,
			statements: previous.statements.map((s) => (s.id === id ? { ...s, ...updates } : s))
		}));
	};

	const handleAddStatement = () => {
		setFormState((previous) => ({
			...previous,
			statements: [...previous.statements, buildStatement()]
		}));
	};

	const handleRemoveStatement = (id: string) => {
		setFormState((previous) => {
			if (previous.statements.length === 1) {
				return previous;
			}
			return {
				...previous,
				statements: previous.statements.filter((s) => s.id !== id)
			};
		});
	};

	const handleSave = () => {
		onSave({
			assignmentName: formState.assignmentName.trim(),
			statements: formState.statements.map((s) => {
				const trimmed = s.value.trim();
				const entity = isDecisionValueEntityRow(s);
				return {
					...s,
					value: trimmed,
					plainTextValue: entity ? undefined : trimmed,
				};
			}),
		});
	};

	const renderStatementRows = () => (
		<Stack spacing={2}>
			{formState.statements.map((statement, index) => {
				const leftDataType = optionDataTypeLabel(
					simpleVariables.find((v) => v.name === statement.variable)?.dataTypeId,
				);
				return (
					<FormStatementContainer key={statement.id} num={index + 1}>
						{onAddSimpleVariable ? (
							<SimpleVariableSelect
								value={statement.variable}
								onChange={(value) => updateStatement(statement.id, { variable: value })}
								simpleVariables={simpleVariables}
								existingVariables={existingVariables}
								onAddSimpleVariable={onAddSimpleVariable}
								platformId={platformId}
								resourceVariableGroups={resourceVariableGroups}
								formulaVariableOptions={formulaVariableOptions}
								resourceScreenFields={resourceScreenFields}
								globalConstantOptions={globalConstantOptions}
								ruleFormulaVariables={ruleFormulaVariables}
								ruleDatabaseSingleRowVariables={ruleDatabaseSingleRowVariables}
								ruleDatabaseMultiRowVariables={ruleDatabaseMultiRowVariables}
							/>
						) : (
							<TextField
								select
								fullWidth
								label="Simple Variable"
								value={statement.variable}
								onChange={(event) => updateStatement(statement.id, { variable: event.target.value })}
							>
								{simpleVariables.map((v) => (
									<MenuItem key={v.id} value={v.name}>
										{v.name}
									</MenuItem>
								))}
							</TextField>
						)}
						<AssignmentOperatorsSelect
							value={statement.operator}
							onChange={(value) => updateStatement(statement.id, { operator: value })}
							options={operatorOptions}
							suppressAutoFetch={suppressAssignmentOperatorsAutoFetch}
						/>
						<FormEntitySelect
							label="Value"
							isValueField
							value={getAssignmentValueFormEntityBinding(statement)}
							onChange={(val: string, category: ResourceCategoryV2, label: string, dataType?: string) => {
								const emptyEntity: Partial<Omit<AssignmentStatementRow, "id">> = {
									valueDatabaseTableFieldId: undefined,
									valueScreenFieldId: undefined,
									valueDatabaseTableFieldName: undefined,
									valueScreenFieldName: undefined,
									valueScreenField: undefined,
									valueScreenTableField: undefined,
									valueSimpleVariableName: undefined,
									valueSimpleVariableId: undefined,
									valueFormulaVariableId: undefined,
									valueFormulaVariableName: undefined,
									valueFormulaVariable: undefined,
									valueDatabaseSingleRowVariableId: undefined,
									valueDatabaseSingleRowVariableName: undefined,
									valueGlobalConstant: undefined,
									valueGlobalConstantName: undefined,
									valueCategory: undefined,
								};
								const trimmed = val.trim();
								if (!trimmed || category === "literal") {
									updateStatement(statement.id, {
										...emptyEntity,
										value: trimmed,
										plainTextValue: trimmed || undefined,
										valueCategory: category,
									});
									return;
								}
								if (
									category === "singleRowDbVariableField" ||
									category === "multiRowDbVariableField"
								) {
									updateStatement(statement.id, {
										...emptyEntity,
										value: val,
										plainTextValue: undefined,
										valueDatabaseTableFieldId: val,
										valueDatabaseTableFieldName: label,
										valueCategory: category,
									});
									return;
								}
								if (category === "screenTableField" || category === "screenField") {
									const screenRef = buildScreenFieldRef(label, dataType);
									updateStatement(statement.id, {
										...emptyEntity,
										value: val,
										plainTextValue: undefined,
										valueScreenFieldId: val,
										valueScreenField: screenRef,
										valueScreenTableField: screenRef,
										valueScreenFieldName: label,
										valueCategory: category,
									});
									return;
								}
								if (category === "simpleVariable") {
									const id = ruleSimpleVariables.find((v) => v.name === val)?.id;
									updateStatement(statement.id, {
										...emptyEntity,
										value: val,
										plainTextValue: undefined,
										valueSimpleVariableId: id,
										valueSimpleVariableName: val,
										valueCategory: category,
									});
									return;
								}
								if (category === "formulaVariable") {
									const id = ruleFormulaVariables.find((v) => v.name === val)?.id;
									updateStatement(statement.id, {
										...emptyEntity,
										value: val,
										plainTextValue: undefined,
										valueFormulaVariableId: id,
										valueFormulaVariableName: val,
										valueFormulaVariable: buildFormulaVariableRef(
											val,
											dataType,
											statement.valueFormulaVariable,
											id,
										),
										valueCategory: category,
									});
									return;
								}
								if (category === "globalConstant") {
									updateStatement(statement.id, {
										...emptyEntity,
										value: val,
										plainTextValue: undefined,
										valueGlobalConstant: val,
										valueGlobalConstantName: label,
										valueCategory: category,
									});
								}
							}}
							valueCategory={inferValueCategoryFromEntityFields(statement)}
							visibleGroups={	
								valueFieldVisibleGroups
							}
							variableGroups={ resourceVariableGroups}
							formulaVariables={formulaVariableOptions}
							singleRowDbVariables={ruleVariableSingleRowTables}
							multiRowDbVariables={ruleVariableMultiRowTables}
							screenFields={mergedScreenFields}
							globalConstants={globalConstantOptions}
							selectedFieldMeta={
								statement.valueScreenFieldId
									? selectedFieldMetaFromScreenField(
											statement.valueScreenTableField ?? statement.valueScreenField,
											statement.valueScreenFieldId,
										)
									: statement.valueFormulaVariableId || statement.valueFormulaVariableName
										? selectedFieldMetaFromFormulaVariable(
												statement.valueFormulaVariable ??
													buildFormulaVariableRef(
														statement.valueFormulaVariableName,
														undefined,
														undefined,
														statement.valueFormulaVariableId,
													),
											)
										: undefined
							}
						/>
						<RaisedTrashIconButton
							onClick={() => handleRemoveStatement(statement.id)}
							disabled={formState.statements.length === 1}
							aria-label={`Remove assignment ${index + 1}`}
						/>
					</FormStatementContainer>
				);
			})}
		</Stack>
	);

	useEffect(() => {
		if (open) {
			setFormState(initialFormState);
		}
	}, [initialFormState, open]);

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Assignment"
			icon={<Assignment />}
			iconColor="#06B097"
			gradient="linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Assignment"
			width={520}
		>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					label="Assignment Name"
					placeholder="e.g., Counting No Shows"
					value={formState.assignmentName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							assignmentName: event.target.value
						}))
					}
					required
					width="100%"
				/>
				<Box sx={{ p: 2 }}>
					{renderStatementRows()}
					<Box sx={{ textAlign: "center", marginTop: 2 }}>
						<AddConditionButton label="Add Assignment" onClick={handleAddStatement} />
					</Box>
				</Box>
			</Stack>
		</BaseForm>
	);
};

export default AssignmentForm;
